/**
 * Per-path visual state (pure; no GL): style defaults, patch semantics and the
 * RGBA32F state-texture layout the shaders read with texelFetch.
 *
 * Texture: width STATE_TEXELS (3), height = number of paths; row = PackedPath.index.
 *   texel 0  head (m, "fully drawn" → lengthM + 1), tail (m), alpha (0..1), pulse (m, −1 = none)
 *   texel 1  pulseSigma (m), width (CSS px), glow (0..1, before --ld-glow-gain), style index
 *   texel 2  trail amount (0..1), comet visibility (0..1), comet scale, drawn length (m)
 */
import type { LightPath, RouteVisualState } from "@/contracts/map";
import type { Layout } from "@/contracts/store";

export const STATE_TEXELS = 3;
export const STATE_FLOATS = STATE_TEXELS * 4;

export type LightStyle = RouteVisualState["style"];
export const STYLES: readonly LightStyle[] = ["hero", "bloom", "preview", "site", "ring"];
export const STYLE_INDEX: Record<LightStyle, number> = { hero: 0, bloom: 1, preview: 2, site: 3, ring: 4 };

/** Comet head per style (spec §D.8): "full" = 6 px core + 28 px halo; "dot" = 3 px head dot; "none". */
export const STYLE_COMET: Record<LightStyle, "full" | "dot" | "none"> = { hero: "full", bloom: "dot", preview: "none", site: "full", ring: "none" };

/** Comet geometry (CSS px, diameters). */
export const COMET_CORE_PX = 6;
export const COMET_HALO_PX = 28;
export const COMET_DOT_PX = 3;
/** Comet fade when `comet` goes false (spec §E.1 at Tarr: core scale 1 → 0.6, alpha → 0 over 420 settle). */
export const COMET_FADE_MS = 420;
export const COMET_FADE_SCALE = 0.6;
/** Trail → core once the head arrives (spec §D.8: 640 settle). */
export const TRAIL_SETTLE_MS = 640;
/** Site light: glow 0.35 at rest, 0.6 while drawing (spec §D.8). The layer adds the difference while the trail is on. */
export const SITE_DRAW_GLOW_BOOST = 0.25;
/** Packet half-width used while pulseSigma is 0 ("auto", the default): spec §E.5 σ = clamp(0.03·L, 150, 1200). */
export const packetSigma = (lengthM: number): number => Math.min(1200, Math.max(150, 0.03 * lengthM));

/** Core width (CSS px) and glow per style (spec §D.8). Only hero has layout variants. */
export function styleDefaults(style: LightStyle, layout: Layout = "desktop"): { width: number; glow: number } {
  switch (style) {
    case "hero":
      return { width: layout === "kiosk" ? 4.5 : layout === "mobile" ? 3 : 3.5, glow: 1 };
    case "bloom":
      return { width: 2, glow: 0.35 };
    case "preview":
      return { width: 2, glow: 0 };
    case "site":
      return { width: 2, glow: 0.35 };
    case "ring":
      return { width: 1.5, glow: 0 };
  }
}

export function styleForKind(kind: LightPath["kind"]): LightStyle {
  return kind === "site" ? "site" : kind === "ring" ? "ring" : "hero";
}

/** The state a path has before anyone sets it: invisible (alpha 0, nothing drawn). */
export function defaultState(kind: LightPath["kind"], layout: Layout = "desktop"): RouteVisualState {
  const style = styleForKind(kind);
  const d = styleDefaults(style, layout);
  return { head: 0, tail: 0, alpha: 0, glow: d.glow, width: d.width, pulse: -1, pulseSigma: 0, comet: false, style };
}

export interface StateEntry {
  s: RouteVisualState;
  widthSet: boolean; // width was given explicitly (layout changes and style changes keep it)
  glowSet: boolean;
}

const num = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

/**
 * Merge a patch. Semantics:
 *  - `style` without `width`/`glow` in the same patch resets width and glow to that style's defaults
 *    (for the current layout). An explicit width/glow is kept until the next style change.
 *  - alpha, glow clamp to 0..1; head/tail clamp to ≥ 0; width ≥ 0; pulse < 0 means "none" (−1).
 *  - Non-finite numbers are ignored (the previous value stays).
 */
export function applyPatch(prev: StateEntry, patch: Partial<RouteVisualState>, layout: Layout = "desktop"): StateEntry {
  const s = { ...prev.s };
  let widthSet = prev.widthSet, glowSet = prev.glowSet;
  if (patch.style && STYLE_INDEX[patch.style] !== undefined && patch.style !== s.style) {
    s.style = patch.style;
    const d = styleDefaults(s.style, layout);
    if (patch.width === undefined) {
      s.width = d.width;
      widthSet = false;
    }
    if (patch.glow === undefined) {
      s.glow = d.glow;
      glowSet = false;
    }
  }
  if (patch.head !== undefined) s.head = Math.max(0, num(patch.head, s.head));
  if (patch.tail !== undefined) s.tail = Math.max(0, num(patch.tail, s.tail));
  if (patch.alpha !== undefined) s.alpha = Math.min(1, Math.max(0, num(patch.alpha, s.alpha)));
  if (patch.glow !== undefined) {
    s.glow = Math.min(1, Math.max(0, num(patch.glow, s.glow)));
    glowSet = true;
  }
  if (patch.width !== undefined) {
    s.width = Math.max(0, num(patch.width, s.width));
    widthSet = true;
  }
  if (patch.pulse !== undefined) {
    const p = num(patch.pulse, s.pulse);
    s.pulse = p < 0 ? -1 : p;
  }
  if (patch.pulseSigma !== undefined) {
    const v = num(patch.pulseSigma, s.pulseSigma);
    s.pulseSigma = v > 0 ? Math.max(1, v) : 0; // 0 = auto: clamp(0.03·L, 150, 1200)
  }
  if (patch.comet !== undefined) s.comet = !!patch.comet;
  return { s, widthSet, glowSet };
}

/** Re-apply layout-dependent defaults (hero width) to entries whose width was not set explicitly. */
export function relayout(e: StateEntry, layout: Layout): StateEntry {
  if (e.widthSet) return e;
  const w = styleDefaults(e.s.style, layout).width;
  return w === e.s.width ? e : { ...e, s: { ...e.s, width: w } };
}

/** Internal (layer-driven) animation values per path. */
export interface Internals {
  trail: number; //      0..1 trail amount (1 while drawing with a comet, eases to 0 on arrival)
  comet: number; //      0..1 comet visibility
  cometScale: number; // 1 → 0.6 while fading
  cometM: number; //     metres where the comet sits (the head while comet = true; frozen while it fades)
}

export const restInternals = (): Internals => ({ trail: 0, comet: 0, cometScale: 1, cometM: 0 });

/** Is the head considered arrived (fully drawn)? Half a metre of slack absorbs rounding in lengthM. */
export const isArrived = (s: RouteVisualState, declaredLengthM: number): boolean => s.head >= declaredLengthM - 0.5;

/** Does this path put anything on screen? */
export function isVisible(s: RouteVisualState, i: Internals): boolean {
  return s.alpha > 0 && (s.head > s.tail || i.comet > 0);
}

/**
 * Write one path's row into the state buffer (Float32Array of rows × STATE_FLOATS).
 * `drawnLengthM` is the packed (drawn) length; `declaredLengthM` the caller's lengthM.
 * Caller metres (head, tail, pulse, pulseSigma) are scaled by drawn/declared so that
 * head = lengthM always means "the whole drawn line" (closed rings gain a closing segment).
 * `visible` multiplies alpha (layer flags: routes / boundary).
 */
export function writeStateRow(
  out: Float32Array,
  row: number,
  s: RouteVisualState,
  i: Internals,
  drawnLengthM: number,
  declaredLengthM: number,
  visible = 1,
): void {
  const o = row * STATE_FLOATS;
  const k = declaredLengthM > 0 ? drawnLengthM / declaredLengthM : 1;
  const arrived = isArrived(s, declaredLengthM);
  out[o] = arrived ? drawnLengthM + 1 : s.head * k;
  out[o + 1] = s.tail * k;
  out[o + 2] = s.alpha * visible;
  out[o + 3] = s.pulse < 0 ? -1 : s.pulse * k;
  out[o + 4] = s.pulseSigma > 0 ? s.pulseSigma * k : packetSigma(drawnLengthM);
  out[o + 5] = s.width;
  out[o + 6] = Math.min(1, s.glow + (s.style === "site" ? SITE_DRAW_GLOW_BOOST * i.trail : 0));
  out[o + 7] = STYLE_INDEX[s.style];
  out[o + 8] = i.trail;
  out[o + 9] = STYLE_COMET[s.style] === "none" ? 0 : i.comet;
  out[o + 10] = i.cometScale;
  out[o + 11] = drawnLengthM;
}
